import React, { useState, useEffect, useRef } from 'react';
import { RefreshCw } from 'lucide-react';
import ResultsTable from './ResultsTable';
import SummaryPanel from './SummaryPanel';
import LimitBanner from '../common/LimitBanner';
import HumanReviewBanner from '../common/HumanReviewBanner';
import { useParams } from 'react-router-dom';
import { updateEvaluation } from '../../services/evaluationService';

const ProgressPanel = ({ totalSegments, processedSegments, onAbort, onRetry, hasError, isRetrying }) => {
  const [displayedProgress, setDisplayedProgress] = useState(0);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailSuccess, setEmailSuccess] = useState(false);
  const emailTimerRef = useRef(null);
  const { id } = useParams();
  
  useEffect(() => {
    if (processedSegments > 0 && totalSegments > 0) {
      const newProgress = Math.min((processedSegments / totalSegments) * 100, 100);
      const progressIncrement = Math.floor(newProgress);
      
      setDisplayedProgress(progressIncrement);
    }
  }, [processedSegments, totalSegments]);

  useEffect(() => {
    const emailAlreadyCollected = localStorage.getItem('emailSent');
    if (!emailAlreadyCollected) { 
      setShowEmailModal(true);
    }
  }, []);

  const handleEmailSubmit = async () => {
    setEmailError('');

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
      setEmailError('Please enter a valid email address.');
      return;
    }
    try {
      await updateEvaluation(id, 'email', email);
      setEmailSuccess(true);
      localStorage.setItem('emailSent', 'true');

      clearTimeout(emailTimerRef.current);
      setTimeout(() => {
        setShowEmailModal(false);
        setEmailSuccess(false);
      }, 1600);
    
    } catch (err) {
      setEmailError('Failed to save email. Please try again.');
    }
  };

  // const handleCloseEmailModal = () => {
  //   setShowEmailModal(false);
  //   setEmail('');
  //   setEmailError('');
  //   setEmailSuccess(false);
  // };

  if (hasError) {
    return (
      <div className="bg-white border rounded-lg p-4 md:p-6 mb-6">
        <h2 className="text-lg font-semibold mb-2">Evaluation Error</h2>
        <p className="text-red-500">
          Failed to evaluate translation. Please try again.
        </p>
        <div className="mt-4">
          <button
            onClick={onRetry}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            <RefreshCw className="w-4 h-4" />
            <span>Retry</span>
          </button>
        </div>
      </div>
    );
  }

  if (isRetrying && !hasError) {
    return (
      <div className="bg-white border rounded-lg p-4 md:p-6 mb-6">
        <h2 className="text-lg font-semibold mb-2">Evaluating translations</h2>
        <p className="text-gray-600 mb-4">
          We're analyzing your content. This may take a few minutes depending on the size of your translation.
        </p>
        <div className="space-y-4">
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span>Segments processed ({processedSegments} of {totalSegments})</span>
              <span>{Math.round(displayedProgress)}%</span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-500 transition-all duration-1000 ease-out"
                style={{ width: `${displayedProgress}%` }}
              />
            </div>
          </div>
        </div>

        {showEmailModal && (
          <div className="mt-6 p-4 bg-[#CAF1D9] border rounded-lg border border-gray-200">
            <h3 className="font-semibold text-base mb-2 text-[#020817]">
              It seems that the evaluation is taking longer than we thought.
            </h3>
            <p className="text-[#6B7280] mb-4">
              Please leave your email, and we will send you the results as soon as the evaluation is finished.
            </p>
            <div className="flex gap-3">
              <input
                type="email"
                className="flex-1 h-9 border border-gray-200 shadow-[0px_1px_2px_0px_#0208170D] max-w-[360px]
                rounded px-3 py-2 placeholder-gray-500 focus:outline-none focus:ring-0"
                placeholder="your@email.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                disabled={emailSuccess}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !emailSuccess) {
                    handleEmailSubmit();
                  }
                }}
              />
              <button
                className="flex items-center justify-center px-4 py-2 bg-gray-900 h-[36px] w-[86px]
                text-white rounded hover:bg-gray-800 focus:outline-none focus:ring-0 disabled:opacity-50"
                onClick={handleEmailSubmit}
                disabled={emailSuccess}
              >
                Submit
              </button>
            </div>
            {emailError && <div className="text-red-500 text-sm mt-2">{emailError}</div>}
            {emailSuccess && (
              <div className="text-green-600 text-sm mt-2">
                Email saved! You'll be notified when it's ready.
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
};

const ProgressPage = ({ 
    estimates, 
    hasError = false, 
    handleEvaluate, 
    setError, 
    sourceLanguage, 
    targetLanguage, 
    processedSegments, 
    limits, 
    evaluationData 
  }) => {
  const [isRetrying, setIsRetrying] = useState(true);
  const [totalSegments, setTotalSegments] = useState(0);
  const [queuedSegments, setQueuedSegments] = useState(0);
  const wasStartedRef = useRef(false);

  useEffect(() => {
    if (hasError) {
      setError({ 
        isOpen: false,         
        message: 'Failed to evaluate translation. Please try again.',
        progressDisplay: true 
      });
    }
  }, [hasError, setError]);

  useEffect(() => {
    setTotalSegments(estimates?.totalSegments ?? 10);
  }, [estimates?.totalSegments]);    

  useEffect(() => {
    if (processedSegments > 0) {
      wasStartedRef.current = true; 
    }

    const isInitial = processedSegments === 0 && !wasStartedRef.current;
    const queued = isInitial ? 0 : Math.max(totalSegments - processedSegments, 0);
    setQueuedSegments(queued);
  }, [processedSegments, totalSegments]);

  const handleRetry = async () => {
    try {
      setIsRetrying(true);
      setError({
        isOpen: false,         
        message: '',
        progressDisplay: false 
      });
      await handleEvaluate();
    } catch (err) {
      console.error('Error retrying evaluation:', err);
      setIsRetrying(false);
    }
  };

  const handleAbort = () => {
    console.log('trying abort evaluation');
  };

  const handleRestart = () => {
    setIsRetrying(true);
    setError(false);
    handleRetry();
  };

  return (
    <div className="px-4 md:px-0">
      <LimitBanner limits={limits} />

      <div className="mt-6">
        <ProgressPanel 
          totalSegments={totalSegments}
          processedSegments={queuedSegments}
          onAbort={handleAbort}
          onRetry={handleRestart}
          hasError={hasError}
          isRetrying={isRetrying}
        />
      </div>
      {!hasError ?
      <>
        <h1 className="text-xl md:text-2xl font-bold mb-6 mt-8">
          Translation Quality Evaluation Results
        </h1>
        <div className="flex flex-col md:flex-row gap-8">
          <div className="flex-grow w-full">
            <ResultsTable
                segmentsCount={estimates?.totalSegments}
                disableShowMore={true}
                hideFilter={true}
                sourceLanguage={sourceLanguage}
                targetLanguage={targetLanguage}
            />
          </div>

          <div className="w-full md:w-80 flex-shrink-0 space-y-6">
            <div className="pointer-events-none">
              <SummaryPanel isSkeletonLoader/>
            </div>
            <HumanReviewBanner />
          </div>
        </div>
      </> 
      : 
      <div className="min-h-[calc(100vh-50rem)]">
      </div>
      }
    </div>
  );
};

export default ProgressPage; 